import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/Base/Dialog";
import { ComponentProps, ReactNode, useEffect, useState } from "react";
import {
  ContainerForm,
  ContainerFormProps,
  ContainerFormValues,
} from "@/components/Layout/ContainerForm";
import { Button, ButtonProps } from "@/components/Base/Button";

export interface ContainerDialogProps<T>
  extends ComponentProps<typeof Dialog>,
    ContainerFormProps<T> {
  trigger?: ReactNode;
  externalTrigger?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  triggerIcon?: ReactNode;
  triggerVariant?: ButtonProps["variant"];
  triggerLabel?: string;
}
export const ContainerDialog = <T extends ContainerFormValues>({
  containerData,
  trigger,
  triggerLabel,
  externalTrigger,
  open,
  onOpenChange,
  triggerIcon,
  triggerVariant,
  onSubmit,
  onCancel,
  ...props
}: ContainerDialogProps<T>) => {
  const [isOpen, setIsOpen] = useState(open ?? false);

  // Sync local state with the controlled `open` prop
  useEffect(() => {
    if (typeof open === "boolean") {
      setIsOpen(open);
    }
  }, [open]);

  // Handle the dialog's open state changes
  const handleOpenChange = (newOpen: boolean) => {
    setIsOpen(newOpen);
    onOpenChange?.(newOpen); // Notify parent if `onOpenChange` is provided
  };

  const handleCancel = () => {
    if (onCancel) onCancel();
    handleOpenChange(false);
  };

  const handleSubmit = async (values: T) => {
    if (onSubmit) {
      await onSubmit(values);
    }
    handleOpenChange(false);
  };

  return (
    <Dialog onOpenChange={handleOpenChange} open={isOpen}>
      {trigger && !externalTrigger ? (
        // Use custom trigger if provided
        <DialogTrigger asChild>{trigger}</DialogTrigger>
      ) : (
        // Default trigger button
        !externalTrigger && (
          <DialogTrigger asChild={true}>
            <Button variant={triggerVariant}>
              {triggerIcon}
              {triggerLabel
                ? triggerLabel
                : containerData
                  ? "Edit Container"
                  : "Create Container"}
            </Button>
          </DialogTrigger>
        )
      )}
      <DialogContent
        className="m-0 flex max-h-screen flex-col gap-1 p-4"
        // The dialog portals to document.body, so it's outside the DOM
        // subtree of whatever renders <ContainerDialog> -- but it's still a
        // React-tree descendant, and React bubbles synthetic events through
        // that tree. When this dialog is the Edit Container form rendered
        // inside a draggable Layout Builder tile (LayoutContainer.tsx), an
        // unguarded mousedown here reaches the tile's react-grid-layout
        // drag-start handler, hijacking double-click/click-and-hold in the
        // Name field (WEBV3-329). Same fix pattern as ContainerMenu's
        // trigger/content guards, scoped to this dialog only.
        onMouseDown={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle>
            {containerData ? "Edit Container" : "Create Container"}
          </DialogTitle>
          <DialogDescription>
            {containerData
              ? `Edit container ${containerData.displayName ?? containerData.id}`
              : "Enter the container name to create a new container"}
          </DialogDescription>
          <ContainerForm
            {...props}
            containerData={containerData}
            onCancel={handleCancel}
            onSubmit={handleSubmit}
          />
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
};
